<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue';
import router from '@/router';
import { Capacitor } from '@capacitor/core';
import { IonContent, IonIcon } from '@ionic/vue';
import { closeOutline, callOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import PreviousLoginModal from '@/modules/auth/components/PreviousLoginModal.vue';
import CustomButton from '@/shared/components/CustomButton.vue';
import ModalThree from '@/shared/components/ui/ModalThree.vue';
import SignInSignUpAlert from '@/shared/components/SignInSignUpAlert.vue';
import SocialLoginButtons from '@/modules/auth/components/SocialLoginButtons.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useTenant from '@/shared/composables/useTenant';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import SignInWithEmailForm from '@/modules/auth/components/SignInWithEmailForm.vue';
import useModal from '@/shared/composables/useModal';
import ShareAppButton from '@/shared/components/share/ShareAppButton.vue';
import InstallApp from '@/shared/components/install/InstallApp.vue';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import useAuthHelpers from '../composables/useAuthHelpers';
import useAuth from '../composables/useAuth';
import { SocialProvider } from '@/shared/interfaces/user';
import { useRoute } from 'vue-router';
import BackButton from '@/shared/components/BackButton.vue';

// Init
const { getTenantConfigAndStyles } = useTenant();
const assetStore = useAssetsStore();
const { tenantConfig, anySocialLoginEnabled, tenantStyles } = getTenantConfigAndStyles();
const { t } = useI18n();
const { loadImage } = useCacheImage();
const logoUrl = ref();
const { detectPreviousLoginMethod } = useAuthHelpers();
const route = useRoute();

const previousLogin = ref<{ provider: string; timestamp: string } | null>(null);
const showPreviousLoginModal = ref(false);

const isSocialProvider = (provider: string) => {
	return ['google', 'facebook', 'apple'].includes(provider);
};

const handleLoginWithPrevious = async () => {
	if (route.query.previousLogin) return;
	if (!previousLogin.value) return;

	const { provider } = previousLogin.value;

	if (isSocialProvider(provider)) {
		const { oAuthLogin } = useAuth();
		if (tenantStyles.value) {
			await oAuthLogin(provider as SocialProvider, tenantStyles.value);
			await router.push('/dashboard');
		}
	} else {
		handleUseOtherMethod();
	}
};

const handleUseOtherMethod = () => {
	showPreviousLoginModal.value = false;
};

onBeforeMount(async () => {
	const detected = await detectPreviousLoginMethod();
	if (detected) {
		previousLogin.value = detected;
		if (!route.query.previousLogin) {
			showPreviousLoginModal.value = true;
		}
	}
});

// Modals
const { isModalOpen, showHideModal, modalTitle, modalContent } = useModal();
const modalState = ref(false);
const modalType = ref('');
const setModal = (state: boolean, type?: string) => {
	modalState.value = state;
	modalType.value = type === 'tos' ? 'tos' : 'pp';
};

watch(
	assetStore,
	async (assetStore) => {
		if (assetStore.logoUrl) {
			let logoImage = assetStore.logoUrl.split('/').pop();
			logoImage = logoImage ?? 'logo';
			logoUrl.value = await loadImage('logo', logoImage, assetStore.logoUrl);
		}
	},
	{ immediate: true },
);

const onLoginSuccess = () => {
	router.push('/profile/additionaldata');
};

const isFormValid = ref(false);
const onFormValidation = (formValid: boolean) => {
	isFormValid.value = formValid;
};

const goTo = async (link: string) => {
	await router.push(link);
};
</script>

<template>
	<ion-modal :is-open="showPreviousLoginModal" :backdrop-dismiss="true" class="modal-wrapper">
		<div class="bg-white rounded-xl p-6 mx-3 flex flex-col">
			<PreviousLoginModal :previous-login="previousLogin" @continue-with-previous="handleLoginWithPrevious" @use-other="handleUseOtherMethod" @on-login-success="onLoginSuccess" />
			<div class="flex w-full items-center justify-center mt-4">
				<div class="flex items-center justify-center w-[40px] h-[40px] rounded-full bg-anykrowd-gray/30">
					<ion-icon class="text-[24px] text-black cursor-pointer" :icon="closeOutline" @click="handleUseOtherMethod" />
				</div>
			</div>
		</div>
	</ion-modal>
	<ion-content>
		<ModalOne :title="modalTitle" :is-open="isModalOpen">
			<template #content>
				<div class="font-sourcesanspro-regular text-sm text-center text-white/50 leading-[18px]">
					{{ modalContent }}
				</div>
			</template>

			<template #actions>
				<div class="flex items-center justify-center">
					<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-white">
						<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="showHideModal(false)"></ion-icon>
					</div>
				</div>
			</template>
		</ModalOne>

		<ModalThree :is-open="modalState">
			<div class="flex w-full items-center justify-center mb-4">
				<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-anykrowd-gray/30 mb-4">
					<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="setModal(false)"></ion-icon>
				</div>
			</div>
			<h1 class="font-sourcesanspro-semibold text-lg">
				{{ modalType === 'tos' ? t('form.terms_and_conditions') : t('form.privacy_policy') }}
			</h1>
			<div v-dompurify-html="modalType === 'tos' ? tenantConfig!.terms_and_conditions : tenantConfig!.privacy_policy" class="text-anykrowd-dark"></div>
		</ModalThree>

		<div class="flex flex-col justify-between">
			<TopBar v-if="tenantConfig && tenantConfig.enable_public_mode" class="px-[34px]">
				<template #left>
					<BackButton to="/dashboard" />
				</template>
			</TopBar>
			<div class="flex flex-col h-full justify-center">
				<div class="flex flex-col justify-between px-[34px]">
					<div class="icon flex justify-center pt-20">
						<img :src="logoUrl" class="max-w-[180px]" />
					</div>
					<div class="pb-4 pt-6 mb-2 rounded-md">
						<h1 class="font-sourcesanspro-bold text-white text-md text-center">
							{{ tenantConfig?.welcome_title }}
						</h1>
						<div class="max-w-xs mx-auto mb-6 text-white text-sm text-center">
							{{ tenantConfig?.welcome_line }}
						</div>
						<div class="flex flex-col justify-center">
							<SignInWithEmailForm @on-form-validation="onFormValidation" />
							<ion-button expand="full" form="signInWithEmailForm" shape="round" size="large" color="primary" :type="isFormValid ? 'submit' : 'button'">
								{{ $t('auth.sign_in_email_page.button') }}
							</ion-button>
							<div class="font-sourcesanspro-semibold text-base2 text-right leading-none">
								<router-link to="/profile/password/reset/request">
									{{ $t('auth.sign_in_email_page.forgot_password') }}
								</router-link>
							</div>
						</div>
					</div>
					<div class="mb-6 px-4">
						<div class="separator" v-if="anySocialLoginEnabled()">
							<h5>
								<span class="font-sourcesanspro-regular text-base text-white">{{ $t(`auth.sign_in_home_page.orSignInWith`) }}</span>
							</h5>
						</div>
					</div>
					<div class="mb-4 rounded-md">
						<div v-if="tenantConfig?.enable_phone_login" class="flex justify-center mt-2 mb-2">
							<CustomButton class="ion-no-margin" fill="outline" @on-click="goTo('/auth/signinwithphone')" text="auth.sign_in_home_page.logInPhone" :iconLeft="callOutline" />
						</div>
						<SocialLoginButtons @on-login-success="onLoginSuccess"></SocialLoginButtons>
					</div>
					<div class="px-4 pb-2 rounded-md">
						<SignInSignUpAlert buttonText="components.sign_up.buttonText" text="components.sign_up.text" link="/auth/signuphome" />
					</div>
					<ShareAppButton />
					<InstallApp />
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
